import React from 'react';
import { BadgeCheck, AlertTriangle, ShieldAlert } from 'lucide-react';

/**
 * Small badge that surfaces the Google address-validation result persisted on a
 * property (`address_verified` + `address_validation_verdict`).
 *
 * State precedence (mutually exclusive):
 *  1. verdict ends with `_CORRECTED` -> "Corrected" (amber)
 *  2. verified === true             -> "Verified" (green)
 *  3. verified === false            -> "Unverified" (slate)
 *  4. otherwise (no validation data, e.g. manual entry) -> renders nothing
 *
 * `compact` renders an icon only (with a title tooltip) for dense layouts such
 * as the list row.
 */
const AddressVerificationBadge = ({ verified, verdict, compact = false }) => {
  const isCorrected =
    typeof verdict === 'string' && verdict.toUpperCase().endsWith('_CORRECTED');

  let state = null;
  if (isCorrected) {
    state = {
      label: 'Corrected',
      Icon: AlertTriangle,
      classes: 'bg-amber-50 text-amber-700 border-amber-200',
      iconClass: 'text-amber-600',
    };
  } else if (verified === true) {
    state = {
      label: 'Verified',
      Icon: BadgeCheck,
      classes: 'bg-green-50 text-[#16A34A] border-green-200',
      iconClass: 'text-[#16A34A]',
    };
  } else if (verified === false) {
    state = {
      label: 'Unverified',
      Icon: ShieldAlert,
      classes: 'bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]',
      iconClass: 'text-[#64748B]',
    };
  }

  if (!state) return null;

  const { label, Icon, classes, iconClass } = state;

  if (compact) {
    return (
      <span
        title={label}
        aria-label={`Address ${label.toLowerCase()}`}
        className="inline-flex items-center"
      >
        <Icon className={`w-3.5 h-3.5 ${iconClass}`} aria-hidden="true" />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${classes}`}
    >
      <Icon className={`w-3.5 h-3.5 ${iconClass}`} aria-hidden="true" />
      {label}
    </span>
  );
};

export default AddressVerificationBadge;
